// apps/web/src/pages/monitoramento/visao-geral-page.tsx — Fase 03, Plano 06 (MON-02).
import React, { useEffect, useState } from 'react';
import {
  ListFilter,
  RefreshCw,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import type { VisaoGeralItem } from '@pieltec/contracts';
import { TablePagination } from '../../components/ui/table-pagination';
import { CampoSelect } from '../../components/ui/campo-select';

function ehVisaoGeralItem(valor: unknown): valor is VisaoGeralItem {
  if (typeof valor !== 'object' || valor === null) return false;
  const item = valor as Record<string, unknown>;
  return (
    typeof item.opId === 'string' && typeof item.opNumero === 'string' &&
    typeof item.clienteNome === 'string' && typeof item.materialNome === 'string' &&
    typeof item.loteNumero === 'string' && typeof item.dataCriacao === 'string' &&
    typeof item.previsaoTermino === 'string' && typeof item.status === 'string' &&
    typeof item.progressoPct === 'number' && typeof item.desvioConsumo === 'string'
  );
}

function extrairResposta(valor: unknown): { itens: VisaoGeralItem[]; total: number } | null {
  if (typeof valor !== 'object' || valor === null) return null;
  const resposta = valor as Record<string, unknown>;
  if (!Array.isArray(resposta.data) || typeof resposta.total !== 'number') return null;
  return { itens: resposta.data.filter(ehVisaoGeralItem), total: resposta.total };
}

export const VisaoGeralProducaoPage: React.FC = () => {
  const [itens, setItens] = useState<VisaoGeralItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFiltro, setStatusFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(30);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (statusFiltro) params.append('status', statusFiltro);

      const res = await fetch(`/api/v1/monitoramento/visao-geral?${params.toString()}`);
      if (res.ok) {
        const json: unknown = await res.json();
        const resposta = extrairResposta(json);
        if (resposta) {
          setItens(resposta.itens);
          setTotal(resposta.total);
        }
      }
    } catch {
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarDados();
  }, [page, pageSize, statusFiltro]);

  const itensFiltrados = itens.filter((item) => {
    const texto = `${item.opNumero} ${item.clienteNome} ${item.materialNome}`.toLowerCase();
    return texto.includes(busca.toLowerCase());
  });

  const contagemTexto = `${total} ${total === 1 ? 'OP' : 'OPs'}`;

  return (
    <div className="flex flex-col h-full space-y-4 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <ListFilter className="w-6 h-6 text-slate-800" />
            Visão Geral da Produção
          </h1>
          <p className="text-sm text-slate-500">
            Acompanhamento consolidado por cliente, criação e previsão de término (MON-02).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 font-mono">{contagemTexto}</span>
          <button
            onClick={() => void carregarDados()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3 rounded-lg border text-xs">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <input
              type="text"
              aria-label="Buscar ordens de produção" placeholder="Buscar por OP, cliente ou material..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <span className="font-semibold text-slate-700">Status:</span>
            <CampoSelect
              aria-label="Situação da ordem"
              size="sm"
              value={statusFiltro}
              onValueChange={(valor) => {
                setStatusFiltro(valor);
                setPage(1);
              }}
              options={[
                { value: '', label: 'Todos' },
                { value: 'CRIADA', label: 'Criada' },
                { value: 'EM_ANDAMENTO', label: 'Em andamento' },
                { value: 'CONCLUIDA', label: 'Concluída' },
              ]}
              className="w-40 text-xs text-slate-800"
            />
          </div>
        </div>
      </div>

      {/* Grade D5 da Visão Geral */}
      <div className="border rounded-lg overflow-x-auto bg-white shadow-xs" tabIndex={0} role="region" aria-label="Grade da visão geral da produção">
        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />
            ))}
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            Nenhuma OP encontrada para os filtros aplicados.
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <th className="py-2.5 px-3">OP</th>
                <th className="py-2.5 px-3">Cliente</th>
                <th className="py-2.5 px-3">Material / Lote</th>
                <th className="py-2.5 px-3 text-center">Criação</th>
                <th className="py-2.5 px-3 text-center">Previsão Término</th>
                <th className="py-2.5 px-3 text-center">Progresso</th>
                <th className="py-2.5 px-3 text-center">Desvio Consumo</th>
                <th className="py-2.5 px-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {itensFiltrados.map((item) => (
                <tr key={item.opId} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-mono font-semibold text-slate-900">
                    {item.opNumero}
                  </td>
                  <td className="py-2.5 px-3 text-slate-800 font-medium">{item.clienteNome}</td>
                  <td className="py-2.5 px-3 text-slate-700">
                    <span className="block truncate max-w-xs">{item.materialNome}</span>
                    <span className="text-[11px] font-mono text-slate-400">Lote: {item.loteNumero}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center font-mono text-slate-600">
                    {item.dataCriacao}
                  </td>
                  <td className="py-2.5 px-3 text-center font-mono text-slate-800 font-semibold">
                    {item.previsaoTermino}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 bg-slate-200 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-slate-900 h-full"
                          style={{ width: `${item.progressoPct}%` }}
                        />
                      </div>
                      <span className="font-mono text-[11px] text-slate-700">
                        {item.progressoPct}%
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {item.desvioConsumo === 'acima' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        <TrendingUp className="w-3 h-3" /> Acima
                      </span>
                    )}
                    {item.desvioConsumo === 'abaixo' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                        <TrendingDown className="w-3 h-3" /> Abaixo
                      </span>
                    )}
                    {item.desvioConsumo === 'dentro' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Minus className="w-3 h-3" /> No Prazo
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border">
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalItems={total}
        itemLabel="OPs"
        onPageChange={setPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
      />
    </div>
  );
};
